import { useState, useEffect, useRef, useCallback } from 'react'
import { db, realtime, supabase } from '../lib/supabase'
import ContextMenu, { useContextMenu } from './ContextMenu'
import { useTranslation, getBrowserLanguage } from '../hooks/useTranslation'
import { useYouTubeShareContext } from '../contexts/YouTubeShareContext'
import { extractYouTubeId, containsYouTubeLink, getYouTubeThumbnail } from './YouTubePlayer'

/**
 * Panel 3: Main Chat Area (v2.0)
 * NOW WITH:
 * - AI Bot mentions (@GJYL-AI)
 * - Message threading
 * - Context menus
 * - Media integration
 * - Auto-translation for messages from different languages
 * - YouTube link detection with "Play Together" prompt
 */
export default function ChatArea({ selectedChannel, selectedSpace, user, profile, isInVoiceCall = false }) {
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [loading, setLoading] = useState(false)
  const [replyingTo, setReplyingTo] = useState(null)
  const [viewingThread, setViewingThread] = useState(null)
  const [userRole, setUserRole] = useState('member')
  const [detectedYouTube, setDetectedYouTube] = useState(null) // { videoId, url }

  const messagesEndRef = useRef(null)
  const subscriptionRef = useRef(null)
  const contextMenu = useContextMenu()

  // Translation support (default ON)
  const autoTranslate = profile?.auto_translate !== false
  const userLanguage = profile?.preferred_language || getBrowserLanguage()
  const { translate, isTranslating } = useTranslation(userLanguage)

  // YouTube sharing context
  const youtubeShare = useYouTubeShareContext()

  // Detect YouTube links as user types
  useEffect(() => {
    if (containsYouTubeLink(newMessage)) {
      const videoId = extractYouTubeId(newMessage)
      if (videoId) {
        setDetectedYouTube({ videoId, url: newMessage })
      }
    } else {
      setDetectedYouTube(null)
    }
  }, [newMessage])

  // Handle "Play Together" button click
  const handlePlayTogether = useCallback(() => {
    if (!detectedYouTube || !youtubeShare) return

    youtubeShare.shareVideo(
      detectedYouTube.videoId,
      detectedYouTube.url,
      'YouTube Video',
      profile?.username || 'Someone'
    )

    // Clear the detected YouTube since we're sharing it
    setDetectedYouTube(null)
  }, [detectedYouTube, youtubeShare, profile])

  // Make reply/thread functions globally accessible for ContextMenu
  useEffect(() => {
    window.replyToMessage = (message) => setReplyingTo(message)
    window.startThread = (message) => setViewingThread(message)

    return () => {
      window.replyToMessage = null
      window.startThread = null
    }
  }, [])

  // Load user role when space changes
  useEffect(() => {
    if (selectedSpace) {
      loadUserRole()
    }
  }, [selectedSpace, user.id])

  // Load messages when channel changes
  useEffect(() => {
    if (selectedChannel) {
      loadMessages()
      subscribeToMessages()
    }

    return () => {
      if (subscriptionRef.current) {
        realtime.unsubscribe(subscriptionRef.current)
      }
    }
  }, [selectedChannel])

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const loadUserRole = async () => {
    const { data, error } = await supabase
      .from('members')
      .select('role')
      .eq('space_id', selectedSpace.id)
      .eq('user_id', user.id)
      .single()

    if (!error && data) {
      setUserRole(data.role)
    }
  }

  const loadMessages = async () => {
    setLoading(true)
    const { data, error } = await db.messages.list(selectedChannel.id)

    if (error) {
      console.error('Error loading messages:', error)
    } else {
      setMessages(data || [])
    }
    setLoading(false)
  }

  const subscribeToMessages = () => {
    subscriptionRef.current = realtime.subscribeToMessages(
      selectedChannel.id,
      (payload) => {
        if (payload.eventType === 'INSERT') {
          // Add new message to the list
          setMessages((prev) => [...prev, payload.new])
        }
        if (payload.eventType === 'UPDATE') {
          // Update message (for edits/deletes)
          setMessages((prev) =>
            prev.map((msg) => (msg.id === payload.new.id ? payload.new : msg))
          )
        }
      }
    )
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()

    if (!newMessage.trim() || isSending) return

    // Check for AI bot mention
    if (newMessage.trim().startsWith('@GJYL-AI')) {
      await handleAIBotMention()
      return
    }

    setIsSending(true)

    try {
      // Detect media URLs
      const mediaInfo = detectMediaUrl(newMessage)

      const messageData = {
        user_id: user.id,
        channel_id: selectedChannel.id,
        content: newMessage.trim(),
        parent_message_id: replyingTo?.id || null,
        ...mediaInfo,
      }

      const { error } = await db.messages.send(messageData)

      if (error) throw error

      setNewMessage('')
      setReplyingTo(null)
    } catch (error) {
      console.error('Error sending message:', error)
      alert('Failed to send message')
    } finally {
      setIsSending(false)
    }
  }

  const handleAIBotMention = async () => {
    const prompt = newMessage.replace('@GJYL-AI', '').trim()

    if (!prompt) {
      alert('Please provide a prompt for the AI assistant')
      return
    }

    setIsSending(true)

    try {
      // Call AI bot Edge Function
      const { data, error } = await supabase.functions.invoke('gjyl-ai-bot', {
        body: {
          prompt,
          channel_id: selectedChannel.id,
          user_id: user.id,
          username: profile.username,
        },
      })

      if (error) throw error

      setNewMessage('')

      // Show a temporary "AI is thinking" message
      if (!data.success) {
        alert(data.error || 'AI request failed')
      }
    } catch (error) {
      console.error('Error invoking AI bot:', error)
      alert('Failed to invoke AI assistant')
    } finally {
      setIsSending(false)
    }
  }

  const detectMediaUrl = (text) => {
    // YouTube detection
    const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    const youtubeMatch = text.match(youtubeRegex)

    if (youtubeMatch) {
      return {
        media_type: 'youtube',
        media_url: youtubeMatch[0],
        media_metadata: { video_id: youtubeMatch[1] },
      }
    }

    // Twitch detection
    const twitchRegex = /(?:https?:\/\/)?(?:www\.)?twitch\.tv\/([a-zA-Z0-9_]+)/
    const twitchMatch = text.match(twitchRegex)

    if (twitchMatch) {
      return {
        media_type: 'twitch',
        media_url: twitchMatch[0],
        media_metadata: { channel: twitchMatch[1] },
      }
    }

    return {}
  }

  // Filter messages based on thread view
  const displayedMessages = viewingThread
    ? messages.filter((msg) => msg.parent_message_id === viewingThread.id || msg.id === viewingThread.id)
    : messages.filter((msg) => !msg.parent_message_id) // Only show root messages

  if (!selectedChannel) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gjyl-dark">
        <div className="text-center">
          <div className="w-20 h-20 glass-strong rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-slate-400 mb-2">No Channel Selected</h3>
          <p className="text-sm text-slate-500">Select a channel to start chatting</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col bg-gjyl-dark">
      {/* Channel Header */}
      <div className="h-14 glass-strong border-b border-gjyl-slate-700/50 px-6 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {viewingThread ? (
            <>
              <button
                onClick={() => setViewingThread(null)}
                className="btn-ghost"
              >
                ← Back
              </button>
              <span className="text-slate-400">🧵</span>
              <h2 className="text-white font-semibold">Thread</h2>
            </>
          ) : (
            <>
              <span className="text-slate-400">
                {selectedChannel.type === 'text' ? '#' : selectedChannel.type === 'voice' ? '🔊' : '📺'}
              </span>
              <h2 className="text-white font-semibold">{selectedChannel.name}</h2>
              {selectedChannel.description && (
                <span className="text-sm text-slate-500">— {selectedChannel.description}</span>
              )}
            </>
          )}
        </div>

        {/* Channel Actions */}
        <div className="flex items-center space-x-2">
          {selectedSpace && !viewingThread && (
            <button
              className="px-3 py-1 glass rounded-lg text-xs text-slate-400 hover:text-white transition-colors"
              onClick={() => {
                const inviteCode = selectedSpace.invite_code
                navigator.clipboard.writeText(inviteCode)
                alert(`Invite code copied: ${inviteCode}`)
              }}
            >
              📋 Invite
            </button>
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto scrollbar-gjyl px-6 py-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="glass-strong rounded-2xl p-6">
              <div className="animate-pulse flex items-center space-x-4">
                <div className="w-10 h-10 bg-gjyl-indigo-500/30 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-gjyl-slate-700 rounded w-32"></div>
                  <div className="h-2 bg-gjyl-slate-800 rounded w-48"></div>
                </div>
              </div>
            </div>
          </div>
        ) : displayedMessages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-slate-500 mb-2">No messages yet</p>
              <p className="text-sm text-slate-600">Be the first to send a message!</p>
            </div>
          </div>
        ) : (
          displayedMessages.map((message, index) => {
            const threadCount = messages.filter(m => m.parent_message_id === message.id).length

            return (
              <Message
                key={message.id}
                message={message}
                isOwn={message.user_id === user.id}
                showAvatar={index === 0 || displayedMessages[index - 1].user_id !== message.user_id}
                channelType={selectedChannel.type}
                threadCount={threadCount}
                onContextMenu={(e) => contextMenu.open(e, 'message', message)}
                onViewThread={() => setViewingThread(message)}
                translate={translate}
                userLanguage={userLanguage}
                autoTranslate={autoTranslate}
              />
            )
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* YouTube Link Detected Prompt */}
      {detectedYouTube && isInVoiceCall && (
        <div className="px-6 py-3 glass-strong border-t border-gjyl-indigo-500/30 bg-gjyl-indigo-500/10">
          <div className="flex items-center space-x-4">
            {/* Thumbnail */}
            <div className="flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden bg-gjyl-slate-800">
              <img
                src={getYouTubeThumbnail(detectedYouTube.videoId, 'default')}
                alt="Video thumbnail"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white font-medium">YouTube video detected</p>
              <p className="text-xs text-slate-400 truncate">
                Would you like to listen together in the call?
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-2">
              <button
                onClick={handlePlayTogether}
                className="px-4 py-2 bg-gjyl-indigo-500 hover:bg-gjyl-indigo-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
                <span>Play Together</span>
              </button>
              <button
                onClick={() => setDetectedYouTube(null)}
                className="p-2 hover:bg-gjyl-slate-700/50 rounded-lg transition-colors text-slate-400 hover:text-white"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* YouTube Link Detected (not in call) */}
      {detectedYouTube && !isInVoiceCall && (
        <div className="px-6 py-2 glass-strong border-t border-gjyl-slate-700/50">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-12 h-9 rounded overflow-hidden bg-gjyl-slate-800">
              <img
                src={getYouTubeThumbnail(detectedYouTube.videoId, 'default')}
                alt="Video thumbnail"
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-xs text-slate-400">
              YouTube link detected. Join a voice call to listen together!
            </p>
            <button
              onClick={() => setDetectedYouTube(null)}
              className="p-1 hover:bg-gjyl-slate-700/50 rounded transition-colors text-slate-500"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Reply Indicator */}
      {replyingTo && (
        <div className="px-6 py-2 glass-strong border-t border-gjyl-slate-700/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-slate-400">
              <span>↩️</span>
              <span>Replying to</span>
              <span className="text-white font-medium">{replyingTo.profiles?.username}</span>
            </div>
            <button
              onClick={() => setReplyingTo(null)}
              className="p-1 hover:bg-gjyl-slate-700/50 rounded transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Message Input */}
      <div className="p-4 glass-strong border-t border-gjyl-slate-700/50">
        <form onSubmit={handleSendMessage} className="flex items-center space-x-3">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={`Message #${selectedChannel.name} (Try @GJYL-AI for AI help)`}
            className="flex-1 input-gjyl"
            disabled={isSending}
          />
          <button
            type="submit"
            disabled={isSending || !newMessage.trim()}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSending ? (
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              'Send'
            )}
          </button>
        </form>
      </div>

      {/* Context Menu */}
      <ContextMenu
        isOpen={contextMenu.isOpen}
        position={contextMenu.position}
        onClose={contextMenu.close}
        type={contextMenu.type}
        target={contextMenu.target}
        currentUser={user}
        currentUserRole={userRole}
        spaceId={selectedSpace?.id}
      />
    </div>
  )
}

// Enhanced Message Component with threading and translation
function Message({ message, isOwn, showAvatar, channelType, threadCount, onContextMenu, onViewThread, translate, userLanguage, autoTranslate }) {
  const sender = message.profiles
  const timestamp = new Date(message.timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })

  const isBot = sender?.is_bot

  // Translation state
  const [translatedText, setTranslatedText] = useState(null)
  const [isTranslatingMsg, setIsTranslatingMsg] = useState(false)

  // Get sender's language (from profile or default to user's language if own message)
  const senderLanguage = sender?.preferred_language || (isOwn ? userLanguage : null)

  // Get short language code
  const getLanguageCode = (lang) => lang?.split('-')[0] || 'en'
  const senderLangCode = getLanguageCode(senderLanguage)
  const userLangCode = getLanguageCode(userLanguage)

  // Determine if translation is needed
  const needsTranslation = autoTranslate && !isOwn && senderLanguage && senderLangCode !== userLangCode && !message.is_deleted

  // Auto-translate when needed
  useEffect(() => {
    if (needsTranslation && !translatedText && translate) {
      setIsTranslatingMsg(true)
      translate(message.content, senderLanguage, userLanguage)
        .then(result => {
          if (result.wasTranslated) {
            setTranslatedText(result.translated)
          }
        })
        .finally(() => setIsTranslatingMsg(false))
    }
  }, [needsTranslation, message.content, senderLanguage, userLanguage, translate, translatedText])

  // Language display names
  const LANG_NAMES = {
    'en': 'English', 'uk': 'Ukrainian', 'ru': 'Russian', 'es': 'Spanish',
    'fr': 'French', 'de': 'German', 'zh': 'Chinese', 'ja': 'Japanese',
    'ko': 'Korean', 'pt': 'Portuguese', 'it': 'Italian', 'pl': 'Polish'
  }

  // Render media embed for media channels
  const renderMediaEmbed = () => {
    if (channelType !== 'media' || !message.media_type) return null

    if (message.media_type === 'youtube') {
      const videoId = message.media_metadata?.video_id
      return (
        <div className="mt-2 rounded-lg overflow-hidden">
          <iframe
            width="560"
            height="315"
            src={`https://www.youtube.com/embed/${videoId}`}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full aspect-video"
          ></iframe>
        </div>
      )
    }

    if (message.media_type === 'twitch') {
      const channel = message.media_metadata?.channel
      return (
        <div className="mt-2 rounded-lg overflow-hidden">
          <iframe
            src={`https://player.twitch.tv/?channel=${channel}&parent=${window.location.hostname}`}
            height="315"
            width="560"
            allowFullScreen
            className="w-full aspect-video"
          ></iframe>
        </div>
      )
    }

    return null
  }

  return (
    <div
      className={`flex items-start space-x-3 ${isOwn ? 'flex-row-reverse space-x-reverse' : ''}`}
      onContextMenu={onContextMenu}
    >
      {/* Avatar */}
      {showAvatar ? (
        <div className="flex-shrink-0 w-10 h-10 rounded-full overflow-hidden">
          {sender?.avatar_url ? (
            <img src={sender.avatar_url} alt={sender.username} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gjyl-slate-700 flex items-center justify-center text-white font-semibold text-sm">
              {sender?.username?.substring(0, 2).toUpperCase() || 'UN'}
            </div>
          )}
          {isBot && (
            <div className="absolute bottom-0 right-0 w-3 h-3 bg-gjyl-indigo-500 rounded-full border-2 border-gjyl-darker"></div>
          )}
        </div>
      ) : (
        <div className="w-10"></div>
      )}

      {/* Message Content */}
      <div className={`flex-1 ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
        {showAvatar && (
          <div className="flex items-center space-x-2 mb-1">
            <span className="text-sm font-semibold text-white">
              {sender?.username || 'Unknown'}
              {isBot && (
                <span className="ml-2 px-1.5 py-0.5 bg-gjyl-indigo-500/20 border border-gjyl-indigo-500/50 rounded text-xs text-gjyl-indigo-400">
                  BOT
                </span>
              )}
            </span>
            <span className="text-xs text-slate-500">{timestamp}</span>
          </div>
        )}

        <div
          className={`
            px-4 py-2 rounded-2xl max-w-2xl
            ${isOwn
              ? 'bg-gjyl-indigo-500 text-white'
              : isBot
              ? 'glass-strong border border-gjyl-indigo-500/30 text-slate-200'
              : 'glass-strong text-slate-200'
            }
            ${message.is_deleted ? 'opacity-50 italic' : ''}
          `}
        >
          <p className="text-sm break-words whitespace-pre-wrap">
            {message.is_deleted ? '[Message deleted]' : message.content}
          </p>

          {/* Show translation below original */}
          {translatedText && !message.is_deleted && (
            <div className="mt-1 pt-1 border-t border-white/10">
              <p className="text-xs text-gjyl-accent">
                {translatedText}
              </p>
              <span className="text-[10px] text-slate-500">
                Translated from {LANG_NAMES[senderLangCode] || senderLangCode}
              </span>
            </div>
          )}

          {/* Translation loading indicator */}
          {isTranslatingMsg && (
            <div className="mt-1 text-[10px] text-slate-500 animate-pulse">
              Translating...
            </div>
          )}

          {renderMediaEmbed()}

          {/* Thread indicator */}
          {threadCount > 0 && (
            <button
              onClick={onViewThread}
              className="mt-2 flex items-center space-x-1 text-xs text-gjyl-accent hover:text-gjyl-indigo-400 transition-colors"
            >
              <span>🧵</span>
              <span>{threadCount} {threadCount === 1 ? 'reply' : 'replies'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
